import React, { useEffect } from 'react'
import { Platform, ScrollView, Text } from 'react-native'
import { useQuery } from '@apollo/client'
import DocletPage from '../../doclets/DocletPage'
import { CLOUDINARY_NAME } from './cloudinary'
import { colors } from '../../doclets/tokens'
import useCurrentUser from '../../hooks/useCurrentUser'
import useFeatureFlag from '../../hooks/useFeatureFlag'
import useTranslation from '../../hooks/useTranslation'
import LoadingScreen from '../LoadingScreen'
import { getShareDocletQuery, ShareDocletData } from './api'

/**
 * The doclet on its own, with no MI6 or sharing chrome: a root-level route
 * beside the app drawer, opened in a new tab from the builder. Shows the saved
 * draft, so editors see what publishing would put live.
 */
const DocletPreviewScreen = ({ route }) => {
  const { t } = useTranslation()
  const id: string = route?.params?.id
  const { currentUser } = useCurrentUser()
  const docletsEnabled = useFeatureFlag('mi6Doclets')
  const canView = !!currentUser?.access?.doclets && docletsEnabled

  const { data, loading, error } = useQuery(getShareDocletQuery, {
    variables: { id },
    fetchPolicy: 'network-only',
    skip: !id || !canView
  })
  const doclet: ShareDocletData | undefined = data?.shareDoclet

  useEffect(() => {
    if (Platform.OS === 'web' && doclet) {
      document.title = doclet.displayName || t('doclets:untitled')
    }
  }, [doclet, t])

  if (!currentUser || loading) {
    return <LoadingScreen />
  }

  if (!canView || error || !doclet) {
    return (
      <Text style={{ padding: 32 }}>{t('doclets:preview:unavailable')}</Text>
    )
  }

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: colors.page }}
      contentContainerStyle={{ paddingVertical: 32 }}
    >
      <DocletPage
        content={doclet.draftContent}
        cloudinaryCloudName={CLOUDINARY_NAME}
        hostMedia={doclet.hostMedia}
      />
    </ScrollView>
  )
}

export default DocletPreviewScreen
